import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import LegalLinks from "../components/legal/LegalLinks";
import { useAuthContext } from "./AuthContext";
import "./LoginScreen.css";

type Step = "email" | "code";

const LoginScreen: React.FC = () => {
  const { t } = useTranslation();
  const { loginOrRegister, verifyCode, pendingEmail } = useAuthContext();

  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const codeRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleEmailSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || isLoading) return;
    setIsLoading(true);
    setErrorMessage(null);
    try {
      const success = await loginOrRegister(trimmed);
      if (success) {
        setStep("code");
      } else {
        setErrorMessage(t("auth.login.emailError"));
      }
    } catch {
      setErrorMessage(t("auth.login.emailError"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const fullCode = code.join("");
    if (fullCode.length !== 6 || isLoading) return;
    setIsLoading(true);
    setErrorMessage(null);
    const success = await verifyCode(fullCode);
    if (!success) {
      setErrorMessage(t("auth.code.error"));
      setCode(["", "", "", "", "", ""]);
      codeRefs.current[0]?.focus();
    }
    setIsLoading(false);
  };

  const handleCodeChange = (index: number, value: string) => {
    if (isLoading) return;
    const digit = value.replace(/\D/g, "").slice(-1);
    const newCode = [...code];
    newCode[index] = digit;
    setCode(newCode);
    setErrorMessage(null);
    if (digit && index < 5) {
      codeRefs.current[index + 1]?.focus();
    }
    if (newCode.every((d) => d !== "") && newCode.join("").length === 6) {
      setTimeout(() => handleCodeSubmit(), 0);
    }
  };

  const handleCodeKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      codeRefs.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    const newCode = ["", "", "", "", "", ""];
    for (let i = 0; i < pasted.length; i++) newCode[i] = pasted[i];
    setCode(newCode);
    setErrorMessage(null);
    const nextEmpty = pasted.length < 6 ? pasted.length : 5;
    codeRefs.current[nextEmpty]?.focus();
    if (pasted.length === 6) {
      setTimeout(() => handleCodeSubmit(), 0);
    }
  };

  return (
    <div className="login-screen">
      <div className="login-card">
        <div className="login-logo-circle">
          <img src="/topbar_logo.svg" alt="Lyra" className="login-logo-img" />
        </div>

        {step === "email" ? (
          <form className="login-form" onSubmit={handleEmailSubmit} noValidate>
            <div className="login-headline">
              <h2 className="login-title">{t("auth.login.title")}</h2>
              <p className="login-subtitle">{t("auth.login.subtitle")}</p>
            </div>

            <div className="login-field">
              <label className="login-field-label" htmlFor="login-email">
                {t("auth.login.emailLabel")}
              </label>
              <input
                id="login-email"
                className="login-input"
                type="email"
                placeholder={t("auth.login.emailPlaceholder")}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setErrorMessage(null);
                }}
                onKeyDown={(e) => e.key === "Enter" && handleEmailSubmit()}
                disabled={isLoading}
                autoFocus
                autoComplete="email"
              />
            </div>

            {errorMessage && (
              <p className="login-error">{errorMessage}</p>
            )}

            <button
              className="login-primary-btn"
              type="submit"
              disabled={!email.trim() || isLoading}
            >
              {isLoading ? (
                <span className="login-spinner" />
              ) : (
                t("auth.login.continue")
              )}
            </button>
          </form>
        ) : (
          <form className="login-form" onSubmit={handleCodeSubmit} noValidate>
            <div className="login-headline">
              <h2 className="login-title">{t("auth.code.title")}</h2>
              <p className="login-subtitle">
                {t("auth.code.subtitlePrefix")}{" "}
                <span className="login-email-highlight">{pendingEmail || email}</span>{" "}
                {t("auth.code.subtitleSuffix")}
              </p>
            </div>

            <div className="login-field">
              <label className="login-field-label">{t("auth.code.label")}</label>
              <div className="login-code-row">
                {code.map((digit, i) => (
                  <input
                    key={i}
                    ref={(el) => { codeRefs.current[i] = el; }}
                    className={`login-code-input${digit ? " login-code-input--filled" : ""}`}
                    type="text"
                    inputMode="numeric"
                    pattern="\d*"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleCodeChange(i, e.target.value)}
                    onKeyDown={(e) => handleCodeKeyDown(i, e)}
                    onPaste={i === 0 ? handleCodePaste : undefined}
                    disabled={isLoading}
                    autoFocus={i === 0}
                    autoComplete="one-time-code"
                  />
                ))}
              </div>
            </div>

            {errorMessage && (
              <p className="login-error">{errorMessage}</p>
            )}

            <button
              className="login-primary-btn"
              type="submit"
              disabled={code.join("").length !== 6 || isLoading}
            >
              {isLoading ? (
                <span className="login-spinner" />
              ) : (
                t("auth.code.confirm")
              )}
            </button>

            <button
              type="button"
              className="login-back-btn"
              onClick={() => {
                setStep("email");
                setCode(["", "", "", "", "", ""]);
                setErrorMessage(null);
              }}
              disabled={isLoading}
            >
              {t("auth.code.back")}
            </button>
          </form>
        )}
      </div>

      {/*
        ⚠️ Not decoration. This screen renders without the app chrome, so its
        footer - and with it the cancellation button (§ 312k Abs. 2 BGB) and the
        DSA notice form (Art. 16 DSA) - is not on the page. For a visitor without
        an account this nav is the only route to either.
      */}
      <LegalLinks
        navClassName="login-legal-links"
        linkClassName="login-legal-link"
      />
    </div>
  );
};

export default LoginScreen;
